import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mail, Loader2, RefreshCw, Send, Pause, X, Edit3,
  CheckCircle2, AlertCircle, Clock, MessageSquare, Eye
} from "lucide-react";

const RepliesPanel = ({ userId }) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [editingReply, setEditingReply] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchReplies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/replies/pending/${userId}`);
      setReplies(res.data.replies || []);
    } catch (e) {
      console.error("Error fetching replies:", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchReplies(); }, [fetchReplies]);

  const handleScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/replies/scan/${userId}`);
      toast.success("Scanning inbox for replies...");
      setTimeout(fetchReplies, 5000);
    } catch (e) {
      const detail = e.response?.data?.detail || "Scan failed";
      toast.error(detail);
    } finally {
      setScanning(false);
    }
  };

  const handleAction = async (replyId, action, body) => {
    setActionLoading(replyId);
    try {
      await axios.post(`${API}/replies/action`, {
        replyId,
        action,
        editedBody: body || null,
      });
      const msgs = {
        send_draft: "Response sent",
        dismiss: "Reply dismissed",
        pause_cadence: "Cadence paused",
      };
      toast.success(msgs[action] || "Done");
      setEditingReply(null);
      fetchReplies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      detected: { label: "New Reply", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
      draft_ready: { label: "Draft Ready", cls: "bg-ism-teal/20 text-ism-teal border-ism-teal/30" },
      responded: { label: "Responded", cls: "bg-green-500/20 text-green-400 border-green-500/30" },
      dismissed: { label: "Dismissed", cls: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
      cadence_paused: { label: "Paused", cls: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    };
    const m = map[status] || map.detected;
    return <Badge className={`${m.cls} border text-xs`}>{m.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-ism-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="replies-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-ism-teal" />
          <h3 className="text-white font-medium">Prospect Replies</h3>
          {replies.length > 0 && (
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
              {replies.length} pending
            </Badge>
          )}
        </div>
        <Button
          onClick={handleScan}
          disabled={scanning}
          size="sm"
          variant="outline"
          className="border-white/10 text-gray-400 hover:text-white text-xs"
          data-testid="scan-replies-btn"
        >
          {scanning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Scan Inbox
        </Button>
      </div>

      {replies.length === 0 ? (
        <div className="glass-card p-6 text-center">
          <Mail className="h-8 w-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No pending replies</p>
          <p className="text-xs text-gray-600 mt-1">Click Scan Inbox to check for new prospect replies</p>
        </div>
      ) : (
        <div className="space-y-3">
          {replies.map((r) => {
            const isEditing = editingReply === r.replyId;
            const isActing = actionLoading === r.replyId;

            return (
              <motion.div
                key={r.replyId}
                className="glass-card p-4 border border-white/10"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`reply-item-${r.replyId}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(r.status)}
                    <span className="text-sm text-white font-medium">{r.senderName || r.senderEmail}</span>
                  </div>
                  <span className="text-xs text-gray-600">
                    {r.detectedAt ? new Date(r.detectedAt).toLocaleString() : ""}
                  </span>
                </div>

                {/* Original context */}
                <p className="text-xs text-gray-600 mb-2">
                  Re: {r.originalSubject || "Campaign email"} (Step {r.originalStepNum || "?"})
                </p>

                {/* Their reply */}
                <div className="bg-white/5 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-500 mb-1">Their reply:</p>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{r.bodyPreview}</p>
                </div>

                {/* AI Draft */}
                {r.draftResponse && (
                  <div className="bg-ism-teal/5 border border-ism-teal/20 rounded-lg p-3 mb-3">
                    <p className="text-xs text-ism-teal mb-1">AI-drafted response:</p>
                    {isEditing ? (
                      <Textarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        rows={4}
                        className="bg-white/5 border-white/10 text-white text-sm resize-none mt-1"
                      />
                    ) : (
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{r.draftResponse}</p>
                    )}
                  </div>
                )}

                {/* Cadence paused indicator */}
                {r.cadencePaused && (
                  <div className="flex items-center gap-1 mb-3">
                    <Pause className="h-3 w-3 text-amber-400" />
                    <span className="text-xs text-amber-400">Cadence auto-paused</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                  {r.draftResponse && (
                    <Button
                      onClick={() => handleAction(r.replyId, "send_draft", isEditing ? editBody : null)}
                      disabled={isActing}
                      size="sm"
                      className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium"
                    >
                      {isActing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                      Send Response
                    </Button>
                  )}
                  {isEditing ? (
                    <Button onClick={() => setEditingReply(null)} size="sm" variant="ghost" className="text-gray-400 text-xs">
                      Cancel
                    </Button>
                  ) : r.draftResponse ? (
                    <Button
                      onClick={() => { setEditingReply(r.replyId); setEditBody(r.draftResponse); }}
                      size="sm" variant="ghost" className="text-gray-400 text-xs"
                    >
                      <Edit3 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => handleAction(r.replyId, "dismiss")}
                    size="sm" variant="ghost" className="text-gray-500 hover:text-red-400 text-xs ml-auto"
                  >
                    <X className="h-3 w-3 mr-1" /> Dismiss
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RepliesPanel;
